/**
 * Secure Within — A 30-Day Attachment Healing Journey (Recovery Trauma™)
 *
 * Understand your attachment patterns, explore where they came from,
 * recognise your triggers and practise more secure ways of connecting.
 *
 * Nothing here diagnoses, scores or labels a member. Attachment patterns are
 * offered for self-exploration, never as a diagnosis, a fixed identity, or an
 * explanation for anybody else's abuse, manipulation or mistreatment.
 * There are no streaks and no deadlines. Every member response is private and
 * is never read, analysed, translated or shared.
 */

import type { Block, Field, RegulateKey } from "@/lib/breaking-free";
export { REGULATE_LINKS } from "@/lib/breaking-free";
export type { Field, RegulateKey } from "@/lib/breaking-free";

/* ------------------------------------------------------------------ */
/* Blocks — the Breaking Free set plus four attachment-specific ones    */
/* ------------------------------------------------------------------ */

export type PatternKey = "secure" | "anxious" | "avoidant" | "fearful";

export type SWBlock =
  | Block
  | {
      type: "attachmentQuiz";
      key: string;
      title: string;
      intro?: string;
      questions: {
        key: string;
        prompt: string;
        options: { label: string; pattern: PatternKey }[];
      }[];
      closing?: string;
    }
  | {
      type: "summary";
      key: string;
      title: string;
      intro?: string;
      sources: { label: string; key: string }[];
      fields?: Field[];
      closing?: string;
    }
  | {
      type: "practiceLink";
      key: string;
      title: string;
      body?: string;
      to: string;
      label: string;
      emoji?: string;
    };

export const PATTERNS: Record<
  PatternKey,
  { name: string; emoji: string; blurb: string }
> = {
  secure: {
    name: "Secure",
    emoji: "💚",
    blurb:
      "Comfortable with both closeness and independence. Needs can be spoken, emotions tend to settle again after difficulty.",
  },
  anxious: {
    name: "Anxious",
    emoji: "💛",
    blurb:
      "A deep wish for closeness alongside a fear of rejection or being left. Reassurance can feel urgent, distance can feel dangerous.",
  },
  avoidant: {
    name: "Avoidant",
    emoji: "💙",
    blurb:
      "Independence feels safest. Emotional closeness can feel uncomfortable, and relying on other people can feel risky.",
  },
  fearful: {
    name: "Fearful / Disorganized",
    emoji: "💜",
    blurb:
      "Wanting connection and distance at the same time. Closeness can feel longed for and frightening in the same moment.",
  },
};

/** Gentle, pattern-aware suggestions — never a verdict about who someone is. */
export const PATTERN_GUIDANCE: Record<PatternKey, { title: string; points: string[] }> = {
  secure: {
    title: "Patterns to keep practising",
    points: [
      "Keep communicating needs early, before they build up.",
      "Notice the boundaries that keep your relationships comfortable.",
      "Stay tuned to emotional balance — yours as well as theirs.",
      "Practise healthy interdependence: close, and still yourself.",
    ],
  },
  anxious: {
    title: "Places you may want extra gentleness",
    points: [
      "Fears about being left, and what they feel like in the body.",
      "The urge for reassurance — and what sits underneath it.",
      "Self-soothing you can reach for before you reach outward.",
      "Self-validation, boundaries and your own sense of worth.",
    ],
  },
  avoidant: {
    title: "Places you may want extra gentleness",
    points: [
      "Noticing emotion earlier, while it's still small.",
      "Recognising withdrawal as protection rather than proof.",
      "Small, safe vulnerability with people who have earned it.",
      "Letting support in without losing your independence.",
    ],
  },
  fearful: {
    title: "Places you may want extra gentleness",
    points: [
      "Safety first — regulating before responding.",
      "Trust, built in very small pieces.",
      "The push-pull, and what each side is protecting.",
      "Grounding practices you can reach for mid-conversation.",
    ],
  },
};

/* ------------------------------------------------------------------ */
/* Phases                                                              */
/* ------------------------------------------------------------------ */

export type SWPhase = {
  key: string;
  name: string;
  range: string;
  from: number;
  to: number;
  blurb: string;
};

export const PHASES: SWPhase[] = [
  {
    key: "patterns",
    name: "Understand My Patterns",
    range: "Days 1–7",
    from: 1,
    to: 7,
    blurb:
      "Awareness first. What closeness and distance feel like for you, what activates you, and what your responses have been protecting.",
  },
  {
    key: "origins",
    name: "Understand Where It Came From",
    range: "Days 8–14",
    from: 8,
    to: 14,
    blurb:
      "Gently, and entirely optional. What you learned about love, safety and needing people — and the story you're allowed to rewrite.",
  },
  {
    key: "practising",
    name: "Practising Security",
    range: "Days 15–21",
    from: 15,
    to: 21,
    blurb:
      "Self-soothing, self-validation, boundaries, expressing needs and safe vulnerability — practised in small, doable pieces.",
  },
  {
    key: "secure",
    name: "Secure Within",
    range: "Days 22–30",
    from: 22,
    to: 30,
    blurb:
      "What safe love feels like, how you want to respond, and staying connected to yourself while being close to someone else.",
  },
];

export function phaseForDay(day: number): SWPhase {
  return PHASES.find((p) => day >= p.from && day <= p.to) ?? PHASES[0];
}

/* ------------------------------------------------------------------ */
/* Before You Begin                                                    */
/* ------------------------------------------------------------------ */

export type SWIntroSection = {
  key: string;
  title: string;
  emoji: string;
  blocks: SWBlock[];
  regulate?: RegulateKey[];
  safety?: string;
};

export const INTRO_SECTIONS: SWIntroSection[] = [
  {
    key: "welcome",
    title: "A word before you begin",
    emoji: "🤍",
    blocks: [
      {
        type: "read",
        key: "sw-welcome-read",
        body: [
          "Attachment is the emotional bond that begins in infancy. How the people around us responded when we were small — whether comfort arrived, and how reliably — shapes our sense of emotional security and quietly influences how we do closeness as adults.",
          "This journey is not about deciding what is wrong with you. It's about understanding your own patterns, the fears underneath them, the needs they were trying to meet, and the more secure responses you can begin to practise.",
          "Thirty stages, at your own pace. Nothing here is a test, and nothing here is a label.",
          "— Jana, Founder, Recovery Trauma™",
        ],
      },
    ],
  },
  {
    key: "four-patterns",
    title: "The four attachment patterns",
    emoji: "🧭",
    blocks: [
      {
        type: "read",
        key: "sw-patterns-read",
        body: [
          "💚 Secure — comfortable with intimacy and with independence, able to communicate needs, and emotions tend to settle again.",
          "💛 Anxious — a strong desire for closeness alongside a fear of rejection or abandonment, with a greater need for reassurance.",
          "💙 Avoidant — a strong emphasis on independence, discomfort with emotional closeness, and difficulty relying on other people.",
          "💜 Disorganized / Fearful-avoidant — a conflicting pull towards connection and towards distance at the same time.",
          "Most people recognise themselves in more than one, and patterns can shift with different people and different seasons of life. These are patterns for self-exploration, not diagnoses and not fixed identities.",
        ],
      },
    ],
  },
  {
    key: "how-it-works",
    title: "How this journey works",
    emoji: "🕊️",
    blocks: [
      {
        type: "read",
        key: "sw-how-read",
        body: [
          "Thirty stages, grouped into four phases. Each stage has a short piece of education, a pause, one or two private exercises, and a reflection to take with you.",
          "Everything you write saves on its own and stays private. You can stop at any point — Save & continue later, Skip for now, and grounding support are always there.",
          "Your attachment patterns did not cause anybody else's behaviour towards you. This work is about your own fears, needs and protective responses — never about blame.",
        ],
      },
    ],
    safety:
      "Secure Within is educational self-help support. It is not therapy, diagnosis, medical advice or emergency care.",
  },
];

/* ------------------------------------------------------------------ */
/* The 30 stages                                                       */
/* ------------------------------------------------------------------ */

export type SWDay = {
  day: number;
  title: string;
  emoji: string;
  focus: string[];
  pause: string;
  blocks: SWBlock[];
  regulate?: RegulateKey[];
  reflection: string;
  affirmation: string;
  tender?: boolean;
};

const q = (key: string, title: string, fields: Field[], intro?: string): SWBlock => ({
  type: "questions",
  key,
  title,
  intro,
  fields,
});

export const DAYS: SWDay[] = [
  /* ---------------- Phase 1 — Understand My Patterns ---------------- */
  {
    day: 1,
    title: "Understanding Attachment",
    emoji: "🌱",
    focus: [
      "Attachment is simply the emotional bond between people. It begins in infancy, with how consistently comfort and safety arrived, and it quietly influences how we experience closeness later on.",
      "It can show up in how much trust comes easily, how much independence feels necessary, how much reassurance we need, what happens in conflict, how freely emotion is expressed, how safe vulnerability feels, and how loud the fear of being left can get.",
      "None of this is fixed, and none of it is a flaw. It's learned — which means it can also be gently practised differently.",
    ],
    pause: "Take one slower breath. Notice what happens in your chest when you read the word 'closeness'.",
    blocks: [
      q(
        "day1-reflections",
        "Private reflections",
        [
          {
            key: "easiest",
            label: "When you think about close relationships, what feels easiest?",
            rows: 4,
          },
          {
            key: "hardest",
            label: "What feels most difficult?",
            rows: 4,
          },
        ],
        "Nobody sees this but you.",
      ),
    ],
    regulate: ["grounding", "breathing"],
    reflection:
      "Noticing how closeness feels for you is the whole of today. Nothing needs solving yet.",
    affirmation: "Understanding myself is not the same as judging myself.",
  },
  {
    day: 2,
    title: "Explore My Attachment Patterns",
    emoji: "🔍",
    focus: [
      "This is an exploration, not a test. There is no score, no result to be stuck with, and no diagnosis.",
      "Answer with whatever feels most true most of the time. If two answers feel true, choose the one your body recognises first.",
    ],
    pause: "There's no right answer here. Honest is enough.",
    blocks: [
      {
        type: "attachmentQuiz",
        key: "day2-attachment-explore",
        title: "Patterns you may recognise",
        intro: "One question at a time. You can change any answer later.",
        questions: [
          {
            key: "closeness",
            prompt: "When somebody I care about wants to get emotionally close…",
            options: [
              { label: "I usually enjoy it and feel settled", pattern: "secure" },
              { label: "I want it badly and worry it won't last", pattern: "anxious" },
              { label: "I start to feel crowded and need space", pattern: "avoidant" },
              { label: "I want it and it frightens me at the same time", pattern: "fearful" },
            ],
          },
          {
            key: "distance",
            prompt: "When somebody I care about becomes distant…",
            options: [
              { label: "I assume they have their own things going on", pattern: "secure" },
              { label: "I feel anxious and want to fix it straight away", pattern: "anxious" },
              { label: "I pull back too and get on with things alone", pattern: "avoidant" },
              { label: "I swing between chasing them and shutting down", pattern: "fearful" },
            ],
          },
          {
            key: "reassurance",
            prompt: "Reassurance…",
            options: [
              { label: "Is nice, and I don't often need it repeated", pattern: "secure" },
              { label: "I need it often, and it wears off quickly", pattern: "anxious" },
              { label: "I'd rather not ask for it at all", pattern: "avoidant" },
              { label: "I need it and struggle to believe it when it comes", pattern: "fearful" },
            ],
          },
          {
            key: "abandonment",
            prompt: "The thought of somebody leaving…",
            options: [
              { label: "Is sad, and I'd still be okay", pattern: "secure" },
              { label: "Feels unbearable", pattern: "anxious" },
              { label: "Is something I prepare for by not depending on them", pattern: "avoidant" },
              { label: "Is terrifying, and part of me expects it", pattern: "fearful" },
            ],
          },
          {
            key: "trust",
            prompt: "Trusting someone…",
            options: [
              { label: "Comes slowly and steadily", pattern: "secure" },
              { label: "Comes fast, then I worry about losing it", pattern: "anxious" },
              { label: "Is hard — I rely on myself", pattern: "avoidant" },
              { label: "Comes and goes, sometimes within a day", pattern: "fearful" },
            ],
          },
          {
            key: "vulnerability",
            prompt: "Letting someone see the tender parts of me…",
            options: [
              { label: "Feels okay with people who have earned it", pattern: "secure" },
              { label: "I do it quickly, hoping to be held", pattern: "anxious" },
              { label: "I avoid it", pattern: "avoidant" },
              { label: "I do it, then want to take it back", pattern: "fearful" },
            ],
          },
          {
            key: "conflict",
            prompt: "In conflict I tend to…",
            options: [
              { label: "Stay in it and repair afterwards", pattern: "secure" },
              { label: "Need it resolved immediately", pattern: "anxious" },
              { label: "Go quiet or leave the room", pattern: "avoidant" },
              { label: "Flip between intense and frozen", pattern: "fearful" },
            ],
          },
          {
            key: "independence",
            prompt: "My independence…",
            options: [
              { label: "Sits comfortably alongside closeness", pattern: "secure" },
              { label: "I'd happily give it up to feel secure", pattern: "anxious" },
              { label: "Is the thing I protect most", pattern: "avoidant" },
              { label: "I cling to it, then feel lonely inside it", pattern: "fearful" },
            ],
          },
          {
            key: "help",
            prompt: "Asking for help…",
            options: [
              { label: "Is something I can do", pattern: "secure" },
              { label: "I do it often and then feel like a burden", pattern: "anxious" },
              { label: "I almost never do", pattern: "avoidant" },
              { label: "I want to and usually don't", pattern: "fearful" },
            ],
          },
          {
            key: "needs",
            prompt: "Saying what I need…",
            options: [
              { label: "Feels doable most of the time", pattern: "secure" },
              { label: "I hint, hoping they'll notice", pattern: "anxious" },
              { label: "I'd rather handle it myself", pattern: "avoidant" },
              { label: "I go back and forth and often say nothing", pattern: "fearful" },
            ],
          },
          {
            key: "affection",
            prompt: "Receiving affection…",
            options: [
              { label: "Feels warm and easy enough", pattern: "secure" },
              { label: "I want more of it, and fear it stopping", pattern: "anxious" },
              { label: "Can feel like too much", pattern: "avoidant" },
              { label: "Feels lovely and unsafe at once", pattern: "fearful" },
            ],
          },
        ],
        closing:
          "These results describe patterns you may recognise in yourself. They are not a diagnosis or a permanent label.",
      },
    ],
    reflection:
      "Whatever you recognised today is information, not identity. It's saved privately so you can compare later.",
    affirmation: "I can look honestly at my patterns without turning them into a verdict.",
  },
  {
    day: 3,
    title: "Closeness & Distance",
    emoji: "🫂",
    focus: [
      "Closeness and distance both make something happen in the body — often before any thought arrives.",
      "Today is about noticing which one activates you more, and what each one actually feels like inside.",
    ],
    pause: "Think of someone you're close to. Where do you feel that in your body right now?",
    blocks: [
      {
        type: "chips",
        key: "day3-closeness",
        title: "When somebody gets emotionally close, I tend to…",
        intro: "Tap anything that feels true. More than one is normal.",
        options: [
          "Move closer",
          "Feel safe",
          "Need reassurance",
          "Become uncomfortable",
          "Pull away",
          "Want closeness and distance at the same time",
          "I'm not sure",
        ],
      },
      q("day3-body", "What it feels like in my body", [
        { key: "close", label: "Closeness feels like…", rows: 3 },
        { key: "distance", label: "Distance feels like…", rows: 3 },
        { key: "activates", label: "Which one activates me more, and how can I tell?", rows: 3 },
      ]),
    ],
    regulate: ["somatic", "grounding"],
    reflection: "Your body has been tracking closeness and distance all along. Today you listened.",
    affirmation: "My body's reactions are information, not misbehaviour.",
  },
  {
    day: 4,
    title: "My Attachment Triggers",
    emoji: "⚡",
    focus: [
      "An attachment trigger is a moment that switches on an old fear — of being left, of being too much, of not mattering, of being trapped.",
      "Mapping one moment slowly shows you the fear underneath the reaction, and the need underneath the fear.",
    ],
    pause: "Choose a moment that still stings a little, not the hardest one you have.",
    blocks: [
      {
        type: "triggerMap",
        key: "day4-trigger-map",
        title: "Trigger mapping",
        intro:
          "This saves into your Trigger Journal too, so all your triggers live in one place.",
      },
      q("day4-attachment-fear", "The attachment layer", [
        { key: "afraid", label: "What was I afraid might happen?", rows: 3 },
        {
          key: "wanted",
          label:
            "What did I want to do? (chase, reassure, withdraw, shut down, please, freeze, become angry, something else)",
          rows: 3,
        },
        { key: "familiar", label: "Was this feeling familiar? Where from?", rows: 3 },
        {
          key: "needed",
          label:
            "What did I actually need? (safety, space, reassurance, connection, validation, boundaries, something else)",
          rows: 3,
        },
      ]),
    ],
    regulate: ["trigger-journal", "breathing"],
    reflection:
      "Naming the fear underneath the reaction is how the reaction slowly stops running the show.",
    affirmation: "Underneath my reaction is a need that deserves care.",
    tender: true,
  },
  {
    day: 5,
    title: "My Core Attachment Beliefs",
    emoji: "🪨",
    focus: [
      "Underneath attachment patterns there are usually a few old beliefs — learned early, rarely examined.",
      "They are not truths about you. They are conclusions a younger part of you drew to stay safe.",
    ],
    pause: "Read the list slowly. Notice which sentence lands somewhere in your body.",
    blocks: [
      {
        type: "chips",
        key: "day5-beliefs",
        title: "What does the wounded part of me believe?",
        intro: "Tap any that feel familiar.",
        options: [
          "People will leave me",
          "I'm not enough",
          "I have to earn love",
          "I can't depend on anyone",
          "I have to handle everything myself",
          "Vulnerability will hurt",
          "I want closeness but it scares me",
        ],
        fields: [{ key: "own", label: "Something else this part believes", rows: 2 }],
      },
      q("day5-belief-story", "Sitting with it gently", [
        { key: "loudest", label: "When is this belief loudest?", rows: 3 },
        { key: "learned", label: "Where might I have learned it?", rows: 3 },
        { key: "needs", label: "What does that part of me need now?", rows: 3 },
      ]),
    ],
    regulate: ["inner-child", "grounding"],
    reflection: "A belief you can see is a belief you can begin to question.",
    affirmation: "What I learned early is not the final word on what I'm worth.",
    tender: true,
  },
  {
    day: 6,
    title: "My Relationship Patterns",
    emoji: "🔁",
    focus: [
      "Patterns are protective. Every one of them started as a way to keep connection safe, or to keep yourself safe inside it.",
      "Recognising them isn't a criticism. It's the beginning of choice.",
    ],
    pause: "Read these as behaviours, not as faults.",
    blocks: [
      {
        type: "chips",
        key: "day6-patterns",
        title: "Patterns I recognise",
        intro: "Tap any you notice in yourself.",
        options: [
          "Reassurance seeking",
          "Checking",
          "People pleasing",
          "Fear of abandonment",
          "Overthinking",
          "Difficulty expressing needs",
          "Emotional withdrawal",
          "Avoiding vulnerability",
          "Shutting down",
          "Push-pull",
          "Difficulty trusting",
          "Fear of losing independence",
        ],
      },
      q("day6-protecting", "What is it protecting?", [
        { key: "strongest", label: "Which pattern shows up most often?", rows: 3 },
        {
          key: "protects",
          label: "What is that behaviour trying to protect me from?",
          rows: 4,
        },
      ]),
    ],
    reflection: "Your patterns have been doing a job. Today you saw what job that was.",
    affirmation: "My protective patterns made sense. I can choose differently now.",
  },
  {
    day: 7,
    title: "My Attachment Map",
    emoji: "🗺️",
    focus: [
      "This brings the week together: one chain, from the moment that lands to the response you'd like to practise.",
      "You can fill it for the pattern that shows up most, and come back for others later.",
    ],
    pause: "One example is enough. Choose one that repeats.",
    blocks: [
      {
        type: "steps",
        key: "my-attachment-map",
        title: "My Attachment Map",
        intro: "Follow the chain slowly — each link makes the next one clearer.",
        steps: [
          {
            title: "Trigger",
            body: "The moment that lands.",
            field: { key: "trigger", label: "What happens?", rows: 2 },
          },
          {
            title: "Attachment fear",
            body: "The fear it switches on.",
            field: { key: "fear", label: "What am I afraid of?", rows: 2 },
          },
          {
            title: "Body response",
            body: "Where it lives physically.",
            field: { key: "body", label: "What happens in my body?", rows: 2 },
          },
          {
            title: "Protective response",
            body: "What I usually do.",
            field: { key: "protective", label: "What do I do?", rows: 2 },
          },
          {
            title: "Underlying need",
            body: "What was actually needed.",
            field: { key: "need", label: "What do I need?", rows: 2 },
          },
          {
            title: "A more secure response I can practise",
            body: "Small and doable, not perfect.",
            field: { key: "secure", label: "What could I try instead?", rows: 3 },
          },
        ],
        closing: "Saved as My Attachment Map. You can return and add to it any time.",
      },
    ],
    reflection: "You now have a map of one pattern, from the outside in.",
    affirmation: "I can see the chain — which means I can change a link in it.",
  },

  /* ---------------- Phase 2 — Where It Came From ---------------- */
  {
    day: 8,
    title: "What I Learned About Love",
    emoji: "🕯️",
    focus: [
      "This phase looks backwards, gently. It is entirely optional — skip anything that feels like too much today.",
      "We're not looking for blame. We're looking at what you learned love required.",
    ],
    pause: "You can answer one line and stop. That counts.",
    blocks: [
      q("day8-learned", "Growing up…", [
        { key: "felt", label: "Growing up, love felt…", rows: 3 },
        { key: "needed", label: "To receive love I needed to…", rows: 3 },
        { key: "upset", label: "When I was upset, the people around me…", rows: 3 },
      ]),
    ],
    regulate: ["grounding", "inner-child"],
    reflection: "What you learned was learned. It wasn't chosen, and it isn't permanent.",
    affirmation: "I can look back without having to stay there.",
    tender: true,
  },
  {
    day: 9,
    title: "Emotional Safety",
    emoji: "🛟",
    focus: [
      "Emotional safety is whether feelings were welcome — whether comfort could be asked for, and what happened when it was.",
      "Some people had islands of safety in difficult places. Both can be true.",
    ],
    pause: "If a name comes up that hurts, you can leave that line blank.",
    blocks: [
      q("day9-safety", "Who and what felt safe", [
        { key: "safe", label: "Who made me feel safe?", rows: 3 },
        { key: "unsafe", label: "Who made me feel unsafe?", rows: 3 },
        { key: "emotions", label: "Were emotions welcomed in my home?", rows: 3 },
        { key: "comfort", label: "Could I ask for comfort? What happened when I did?", rows: 3 },
      ]),
    ],
    regulate: ["breathing", "grounding"],
    reflection: "Emotional safety can be built now, even if it wasn't there then.",
    affirmation: "My feelings are allowed to take up space.",
    tender: true,
  },
  {
    day: 10,
    title: "Consistency",
    emoji: "🌗",
    focus: [
      "Unpredictability shapes attachment as strongly as absence does. When care comes and goes, the nervous system learns to watch.",
      "Noticing what was consistent — and what wasn't — often explains a lot about your alertness today.",
    ],
    pause: "Think about affection, attention and emotional availability separately.",
    blocks: [
      q("day10-consistency", "Steady and unsteady", [
        { key: "consistent", label: "What felt consistent?", rows: 3 },
        { key: "unpredictable", label: "What felt unpredictable?", rows: 3 },
        { key: "affection", label: "Affection…", rows: 2 },
        { key: "attention", label: "Attention…", rows: 2 },
        { key: "available", label: "Emotional availability…", rows: 2 },
      ]),
    ],
    reflection: "Watchfulness is not paranoia. It's what inconsistency teaches.",
    affirmation: "I learned to watch. I can also learn to rest.",
  },
  {
    day: 11,
    title: "Separation, Rejection & Abandonment",
    emoji: "🌊",
    focus: [
      "For many people, someone leaving means far more than the leaving itself. It carries an older meaning.",
      "Naming that meaning takes some of its power back.",
    ],
    pause: "Go slowly here. Grounding first is a good idea if this feels heavy.",
    blocks: [
      q("day11-leaving", "What leaving means", [
        { key: "means", label: "What does somebody leaving mean to me emotionally?", rows: 4 },
        { key: "inside", label: "What happens inside me when I think about rejection?", rows: 4 },
      ]),
    ],
    regulate: ["grounding", "nervous-system-reset"],
    reflection: "The fear is old. You are not in that old moment now.",
    affirmation: "I can feel the fear of being left without it deciding what I do.",
    tender: true,
  },
  {
    day: 12,
    title: "What Younger Me Needed",
    emoji: "🧸",
    focus: [
      "Turning towards the younger version of you isn't indulgent. It's where a lot of the repair actually happens.",
      "You can give some of what was missing now, in small ways.",
    ],
    pause: "Picture yourself at the age this feeling belongs to.",
    blocks: [
      q("day12-younger", "Younger me", [
        { key: "hear", label: "I needed to hear…", rows: 3 },
        { key: "someone", label: "I needed someone to…", rows: 3 },
        { key: "feel", label: "I needed to feel…", rows: 3 },
        { key: "understood", label: "I wish somebody had understood…", rows: 3 },
        { key: "today", label: "Today I can begin giving myself…", rows: 3 },
      ]),
    ],
    regulate: ["inner-child", "affirmations"],
    reflection: "What you needed then still matters. You're allowed to give it to yourself now.",
    affirmation: "I can be the steady presence I once needed.",
    tender: true,
  },
  {
    day: 13,
    title: "Inner Child & Attachment",
    emoji: "💛",
    focus: [
      "Attachment work and inner child work meet here. Rather than repeating the practices, this stage sends you to the ones already waiting for you.",
      "Come back afterwards and write down anything you want to keep.",
    ],
    pause: "Choose one practice. One is plenty.",
    blocks: [
      {
        type: "practiceLink",
        key: "day13-inner-child-link",
        title: "Continue with an Inner Child practice",
        body: "Your Inner Child practices live in Regulate — they're already set up for this.",
        to: "/inner-child",
        label: "Continue with an Inner Child Practice",
        emoji: "🧸",
      },
      q("day13-after", "After the practice", [
        { key: "noticed", label: "What did I notice?", rows: 3 },
        { key: "keep", label: "Anything I want to keep from that?", rows: 3 },
      ]),
    ],
    regulate: ["inner-child"],
    reflection: "Meeting that part of you gently is the practice. There's no other goal.",
    affirmation: "That part of me is not a problem to fix.",
  },
  {
    day: 14,
    title: "Rewrite My Attachment Story",
    emoji: "✍️",
    focus: [
      "You've carried a story about what love is and what needing someone costs. Today you write it down — and then write the one you're learning.",
      "This is one of the pieces you'll come back to on Day 28.",
    ],
    pause: "Write the first version quickly, without editing it kindly.",
    blocks: [
      q("day14-story-learned", "The story I learned", [
        { key: "love", label: "Love means…", rows: 2 },
        { key: "people", label: "People eventually…", rows: 2 },
        { key: "need", label: "If I need someone…", rows: 2 },
        { key: "keep", label: "To keep someone close I have to…", rows: 2 },
        { key: "hurt", label: "When somebody hurts me I…", rows: 2 },
      ]),
      q("my-attachment-story", "The story I'm learning", [
        { key: "love", label: "Love can be…", rows: 2 },
        { key: "safe", label: "Safe connection can feel…", rows: 2 },
        { key: "allowed", label: "I am allowed to need…", rows: 2 },
        { key: "dont", label: "I don't have to…", rows: 2 },
        { key: "want", label: "I want my relationships to…", rows: 2 },
      ]),
    ],
    reflection: "Both stories are yours. Only one of them has to keep running things.",
    affirmation: "I am allowed to learn a different story about love.",
  },

  /* ---------------- Phase 3 — Practising Security ---------------- */
  {
    day: 15,
    title: "Self-Soothing",
    emoji: "🌊",
    focus: [
      "Self-soothing is not a replacement for connection. It's what makes connection less urgent — so you can respond rather than react.",
      "The practices that work are the ones your body actually settles with, not the ones that should work.",
    ],
    pause: "Think of a time you calmed down. What helped?",
    blocks: [
      {
        type: "toolkit",
        key: "my-attachment-regulation-toolkit",
        title: "My Attachment Regulation Toolkit",
        intro:
          "Choose the practices that genuinely settle you. Each one opens the real practice in Regulate.",
        examples: [],
      },
    ],
    regulate: ["grounding", "breathing", "somatic", "meditations"],
    reflection: "You now have somewhere to go when the feeling gets big.",
    affirmation: "I can soothe myself and still want closeness.",
  },
  {
    day: 16,
    title: "Urgency or Underlying Need?",
    emoji: "⏳",
    focus: [
      "When attachment fear is activated, a thought can feel like an emergency: I have to know now, I have to fix this now.",
      "Slowing that chain down usually reveals a real need that can be met without abandoning yourself.",
    ],
    pause: "Bring to mind an urgent thought you've had recently.",
    blocks: [
      {
        type: "steps",
        key: "day16-urgency",
        title: "From urgency to need",
        steps: [
          {
            title: "Urgent thought",
            body: "The one that demands action immediately.",
            field: { key: "thought", label: "The thought is…", rows: 2 },
          },
          {
            title: "What am I afraid of?",
            body: "Underneath the urgency.",
            field: { key: "fear", label: "I'm afraid that…", rows: 2 },
          },
          {
            title: "Underlying need",
            body: "The real, human need.",
            field: { key: "need", label: "What I actually need is…", rows: 2 },
          },
          {
            title: "How can I support that need without abandoning myself?",
            body: "Something you can do today.",
            field: { key: "support", label: "I could…", rows: 3 },
          },
        ],
      },
    ],
    regulate: ["breathing", "regulation-compass"],
    reflection: "Urgency is a feeling, not an instruction.",
    affirmation: "I can wait with a feeling without acting on it immediately.",
  },
  {
    day: 17,
    title: "Self-Validation",
    emoji: "🪞",
    focus: [
      "Often the thing we most want to hear from someone else is the thing we've never said to ourselves.",
      "Self-validation doesn't replace being cared for. It means you're not waiting, empty, for it to arrive.",
    ],
    pause: "What do you wish someone would say to you today?",
    blocks: [
      q("day17-validation", "Saying it to myself", [
        { key: "hoping", label: "What am I hoping somebody else will tell me?", rows: 3 },
        { key: "myself", label: "What could I say to myself instead?", rows: 3 },
      ]),
      {
        type: "affirmations",
        key: "my-secure-affirmations",
        title: "Words I can give myself",
        intro: "Choose the ones that feel true enough to say.",
        options: [
          "My feelings make sense.",
          "I don't have to earn care.",
          "I can be loved and still need space.",
          "Needing people does not make me too much.",
          "I am allowed to take up room in my relationships.",
          "I can hold my own hand while I wait.",
        ],
      },
    ],
    regulate: ["affirmations"],
    reflection: "Being your own witness is a skill, and you just practised it.",
    affirmation: "I can validate myself while still welcoming support.",
  },
  {
    day: 18,
    title: "Boundaries",
    emoji: "🧱",
    focus: [
      "A boundary protects the relationship as much as it protects you. It isn't a punishment and it isn't an ultimatum.",
      "Notice especially what you tolerate because losing someone feels worse than the tolerating.",
    ],
    pause: "Boundaries can be small. Small ones count.",
    blocks: [
      q("my-boundaries", "My Boundaries", [
        { key: "okay", label: "What feels okay for me?", rows: 3 },
        { key: "notokay", label: "What doesn't feel okay?", rows: 3 },
        {
          key: "tolerate",
          label: "What do I tolerate out of fear of losing someone?",
          rows: 3,
        },
        {
          key: "protects",
          label: "What boundary would protect me without punishing them?",
          rows: 3,
        },
        { key: "communicate", label: "How could I communicate it?", rows: 3 },
      ]),
    ],
    reflection: "A boundary is a piece of information, offered kindly.",
    affirmation: "I can protect myself without closing my heart.",
  },
  {
    day: 19,
    title: "Expressing My Needs",
    emoji: "🗣️",
    focus: [
      "Most of us were never taught the sentence. Having a shape to fill in makes speaking up far less frightening.",
      "Build one for a real situation you're in, then write your own version in your own words.",
    ],
    pause: "Think of something you've been hinting at rather than saying.",
    blocks: [
      {
        type: "steps",
        key: "day19-needs-builder",
        title: "Build the sentence",
        steps: [
          { title: "I feel…", body: "Name the feeling.", field: { key: "feel", label: "I feel…", rows: 2 } },
          { title: "When…", body: "The specific situation.", field: { key: "when", label: "When…", rows: 2 } },
          { title: "What I need is…", body: "The need, plainly.", field: { key: "need", label: "What I need is…", rows: 2 } },
          {
            title: "What I would like to ask is…",
            body: "The request, small and clear.",
            field: { key: "ask", label: "What I would like to ask is…", rows: 2 },
          },
        ],
      },
      q("my-communication-tools", "My own examples", [
        { key: "mine", label: "In my own words…", rows: 4 },
        { key: "practice", label: "Who could I practise this with?", rows: 2 },
      ]),
    ],
    reflection: "Saying a need out loud is a practice, not a personality trait.",
    affirmation: "My needs are speakable.",
  },
  {
    day: 20,
    title: "Safe Vulnerability",
    emoji: "🌤️",
    focus: [
      "Vulnerability is not telling everyone everything. It's letting the right people see something true, in a size you can manage.",
      "Trust is earned in evidence, not promised in advance.",
    ],
    pause: "Who has actually shown up for you, repeatedly?",
    blocks: [
      q("day20-vulnerability", "Vulnerability, my size", [
        { key: "means", label: "What does vulnerability mean to me?", rows: 3 },
        {
          key: "afraid",
          label: "What am I afraid could happen if somebody really sees me?",
          rows: 3,
        },
        { key: "earned", label: "Who has earned access to that part of me?", rows: 3 },
        {
          key: "small",
          label: "What would a small, safe piece of vulnerability look like this week?",
          rows: 3,
        },
      ]),
    ],
    regulate: ["grounding"],
    reflection: "Small and safe is the whole method.",
    affirmation: "I can be open with people who have earned it.",
  },
  {
    day: 21,
    title: "Connection Without Losing Myself",
    emoji: "🧭",
    focus: [
      "Independence: I manage alone. Dependence: I need you to hold me up. Interdependence: we lean, and we both still stand.",
      "Interdependence is the aim — close, and still recognisably yourself.",
    ],
    pause: "Which of the three do you tend towards under stress?",
    blocks: [
      {
        type: "chips",
        key: "day21-style",
        title: "Under stress I lean towards…",
        options: ["Independence", "Dependence", "Interdependence", "It changes"],
      },
      q("day21-connected", "Staying myself", [
        {
          key: "how",
          label: "How can I stay connected to myself while loving somebody else?",
          rows: 4,
        },
        { key: "signs", label: "What are my early signs of disappearing?", rows: 3 },
      ]),
    ],
    reflection: "You can be close to someone and not lose the thread back to yourself.",
    affirmation: "I can love someone and stay connected to me.",
  },

  /* ---------------- Phase 4 — Secure Within ---------------- */
  {
    day: 22,
    title: "What Does Safe Love Feel Like?",
    emoji: "🤍",
    focus: [
      "Many people can describe unsafe love in detail and go blank on safe love. It helps to describe it deliberately.",
      "Include the body. Safety is a physical experience as much as an emotional one.",
    ],
    pause: "If you've never felt it, describe what you imagine it would feel like.",
    blocks: [
      q("day22-safe-love", "Safe love", [
        { key: "feels", label: "Safe love feels…", rows: 3 },
        { key: "notrequire", label: "Safe love does not require me to…", rows: 3 },
        { key: "body", label: "In safe love, my body feels…", rows: 3 },
        { key: "myself", label: "I can be myself by…", rows: 3 },
      ]),
    ],
    reflection: "Now you have something to recognise, rather than only something to avoid.",
    affirmation: "Love is allowed to feel calm.",
  },
  {
    day: 23,
    title: "My Relationship Green Flags",
    emoji: "💚",
    focus: [
      "Green flags are the ordinary, repeated behaviours that make a relationship feel safe.",
      "Choose the ones that matter most to you, and add your own.",
    ],
    pause: "Think about what makes you exhale around someone.",
    blocks: [
      {
        type: "chips",
        key: "my-green-flags",
        title: "My green flags",
        intro: "Tap the ones that matter to you, then add your own.",
        options: [
          "Consistency",
          "Accountability",
          "Respect",
          "Emotional availability",
          "Communication",
          "Boundaries honoured",
          "Kindness",
          "Independence respected",
          "Repair after conflict",
        ],
        fields: [
          { key: "own1", label: "My own green flag", rows: 2 },
          { key: "own2", label: "Another one", rows: 2 },
        ],
      },
    ],
    reflection: "Knowing what good looks like makes it much easier to notice when it's missing.",
    affirmation: "I know what safety looks like, and I'm allowed to want it.",
  },
  {
    day: 24,
    title: "Responding Instead of Reacting",
    emoji: "⏸️",
    focus: [
      "The pause is the whole skill. Everything else follows from it.",
      "Work through a real moment — and regulate before you decide how you want to respond.",
    ],
    pause: "Pick a moment from the last week, not the biggest one you have.",
    blocks: [
      {
        type: "steps",
        key: "day24-respond",
        title: "Pause, then choose",
        steps: [
          {
            title: "Something happened",
            body: "Just the facts.",
            field: { key: "happened", label: "What happened?", rows: 2 },
          },
          { title: "Pause", body: "One slower breath before the next line." },
          {
            title: "What am I feeling?",
            body: "Name it as plainly as you can.",
            field: { key: "feeling", label: "I feel…", rows: 2 },
          },
          {
            title: "What fear was activated?",
            body: "The attachment layer.",
            field: { key: "fear", label: "The fear was…", rows: 2 },
          },
          {
            title: "What do I need?",
            body: "For yourself, first.",
            field: { key: "need", label: "I need…", rows: 2 },
          },
          {
            title: "Regulate before deciding",
            body: "Use a short practice below, then come back to the last line.",
          },
          {
            title: "How do I want to respond?",
            body: "Chosen, not automatic.",
            field: { key: "respond", label: "I want to respond by…", rows: 3 },
          },
        ],
      },
    ],
    regulate: ["breathing", "grounding", "nervous-system-reset"],
    reflection: "A pause is not passivity. It's where choice lives.",
    affirmation: "I can feel it fully and still choose my response.",
  },
  {
    day: 25,
    title: "Staying Connected to Myself",
    emoji: "🌿",
    focus: [
      "Attachment fear often quietly shrinks a life — interests, opinions, friendships, time alone.",
      "Knowing what's yours makes it much harder to lose.",
    ],
    pause: "What did you love before you were worried about losing anyone?",
    blocks: [
      q("day25-self", "Who I am outside relationships", [
        { key: "matters", label: "What matters to me?", rows: 3 },
        { key: "enjoy", label: "What do I enjoy?", rows: 3 },
        { key: "values", label: "My values are…", rows: 3 },
        {
          key: "disappear",
          label: "Which parts of me disappear when I'm afraid of losing someone?",
          rows: 3,
        },
        { key: "protect", label: "How can I protect those parts?", rows: 3 },
      ]),
    ],
    reflection: "Staying yourself is not a betrayal of closeness. It's what makes closeness safe.",
    affirmation: "I am a whole person inside my relationships.",
  },
  {
    day: 26,
    title: "Receiving Love & Support",
    emoji: "🫶",
    focus: [
      "Receiving can be harder than giving — especially if care once came with a cost attached.",
      "Practising receiving is part of practising security.",
    ],
    pause: "Notice what happens in your chest when someone is kind to you.",
    blocks: [
      q("day26-receiving", "Letting it in", [
        { key: "happens", label: "What happens in me when somebody genuinely supports me?", rows: 3 },
        {
          key: "owing",
          label: "Can I receive care without feeling I owe something back?",
          rows: 3,
        },
        { key: "difficult", label: "What makes receiving difficult?", rows: 3 },
      ]),
    ],
    reflection: "You're allowed to be cared for without earning it.",
    affirmation: "I can receive care without paying for it.",
  },
  {
    day: 27,
    title: "Choosing Safe Relationships",
    emoji: "🔑",
    focus: [
      "Choosing is a skill: noticing what settles you, noticing what activates an old wound, and knowing what you won't explain away any more.",
      "This is about your own standards — not about diagnosing anybody else.",
    ],
    pause: "Be honest about what you've been explaining away.",
    blocks: [
      q("day27-choosing", "What I'm choosing from now", [
        { key: "safe", label: "What behaviour makes me feel safe?", rows: 3 },
        { key: "activates", label: "What activates old wounds in me?", rows: 3 },
        { key: "nonneg", label: "My non-negotiables are…", rows: 3 },
        { key: "noexcuse", label: "What will I no longer explain away?", rows: 3 },
        { key: "qualities", label: "The qualities I want are…", rows: 3 },
      ]),
    ],
    reflection: "Standards are not walls. They're directions.",
    affirmation: "I get to choose who has access to me.",
  },
  {
    day: 28,
    title: "My New Attachment Story",
    emoji: "📖",
    focus: [
      "On Day 14 you wrote the story you learned and the story you were beginning to learn. Read them again.",
      "Notice what has moved, even slightly. Then write it as it stands today.",
    ],
    pause: "Read your earlier answers before writing anything new.",
    blocks: [
      {
        type: "summary",
        key: "day28-review",
        title: "What you wrote on Day 14",
        sources: [
          { label: "The story I learned", key: "day14-story-learned" },
          { label: "The story I'm learning", key: "my-attachment-story" },
        ],
      },
      q("day28-new-story", "My story today", [
        { key: "change", label: "Would I change anything now?", rows: 3 },
        { key: "version", label: "My new version…", rows: 6 },
      ]),
    ],
    reflection: "Development is often a few words, not a transformation. A few words is real.",
    affirmation: "My story is still being written, and I'm the one writing it.",
  },
  {
    day: 29,
    title: "My Secure Relationship Blueprint",
    emoji: "🏛️",
    focus: [
      "Your own blueprint, drawn from your answers across this journey.",
      "Fill in anything that's still blank. This one is worth keeping somewhere you'll see it.",
    ],
    pause: "This is yours to keep. Write it the way you'd want to read it on a hard day.",
    blocks: [
      {
        type: "summary",
        key: "day29-sources",
        title: "Drawn from your journey",
        intro: "Your own words from earlier stages, gathered here.",
        sources: [
          { label: "My Attachment Map", key: "my-attachment-map" },
          { label: "My Boundaries", key: "my-boundaries" },
          { label: "My green flags", key: "my-green-flags" },
          { label: "My story today", key: "day28-new-story" },
        ],
      },
      q("my-secure-blueprint", "My Secure Relationship Blueprint", [
        { key: "feel", label: "I want relationships to feel…", rows: 2 },
        { key: "needs", label: "My needs matter because…", rows: 2 },
        { key: "boundaries", label: "My boundaries are…", rows: 3 },
        { key: "triggers", label: "My triggers include…", rows: 3 },
        { key: "whentriggered", label: "When triggered, I can…", rows: 3 },
        { key: "communicate", label: "I communicate my needs by…", rows: 3 },
        { key: "green", label: "My green flags are…", rows: 3 },
        { key: "warning", label: "My warning signs are…", rows: 3 },
        { key: "without", label: "I can love someone without…", rows: 2 },
        { key: "connected", label: "I stay connected to myself by…", rows: 3 },
      ]),
    ],
    reflection: "Saved permanently in My Secure Within Journey — yours to return to.",
    affirmation: "I know what I'm building towards.",
  },
  {
    day: 30,
    title: "Secure Within",
    emoji: "🤍",
    focus: [
      "You've spent 30 stages understanding your attachment patterns and practising more secure ways of connecting.",
      "You can continue returning to these practices whenever you need them — this journey stays open.",
    ],
    pause: "Before the final reflection, notice what's different about how you're sitting with all this.",
    blocks: [
      q("my-secure-within-closing", "Closing reflection", [
        { key: "understand", label: "What I understand now…", rows: 3 },
        { key: "earlier", label: "Patterns I recognise earlier than I used to…", rows: 3 },
        { key: "triggers", label: "My biggest triggers are…", rows: 3 },
        { key: "regulate", label: "What helps me regulate…", rows: 3 },
        { key: "needs", label: "What I've learned about my needs and boundaries…", rows: 3 },
        { key: "closeness", label: "What safe closeness means to me now…", rows: 3 },
        { key: "future", label: "What I want future relationships to feel like…", rows: 3 },
        {
          key: "practise",
          label: "One secure response I want to keep practising…",
          rows: 3,
        },
      ]),
      {
        type: "summary",
        key: "my-secure-within-plan",
        title: "🤍 My Secure Within Plan",
        intro: "Everything you've gathered, in one place.",
        sources: [
          { label: "Patterns I recognise", key: "day6-patterns" },
          { label: "My triggers", key: "day4-trigger-map" },
          { label: "Core fears", key: "day5-beliefs" },
          { label: "Body responses", key: "day3-body" },
          { label: "Protective responses", key: "day6-protecting" },
          { label: "Underlying needs", key: "day4-attachment-fear" },
          { label: "Self-soothing tools", key: "my-attachment-regulation-toolkit" },
          { label: "My boundaries", key: "my-boundaries" },
          { label: "Communication tools", key: "my-communication-tools" },
          { label: "Green flags", key: "my-green-flags" },
          { label: "Relationship blueprint", key: "my-secure-blueprint" },
          { label: "My new attachment story", key: "day28-new-story" },
        ],
        closing:
          "This plan stays with you. Come back to any stage whenever it would help — nothing here expires.",
      },
    ],
    reflection:
      "You've spent 30 stages understanding your attachment patterns and practising more secure ways of connecting. You can continue returning to these practices whenever you need them.",
    affirmation: "I am learning to be secure within myself.",
  },
];

export function dayByNumber(n: number): SWDay | undefined {
  return DAYS.find((d) => d.day === n);
}

/* ------------------------------------------------------------------ */
/* Phase reflections                                                   */
/* ------------------------------------------------------------------ */

export const WEEK_REFLECTION_DAYS = [7, 14, 21, 30];

export const WEEK_REFLECTION_FIELDS: Field[] = [
  { key: "noticed", label: "What have I noticed about myself in this phase?", rows: 3 },
  { key: "gentle", label: "Where do I want to be gentler with myself?", rows: 3 },
  { key: "carry", label: "What do I want to carry into the next phase?", rows: 3 },
];

/* ------------------------------------------------------------------ */
/* After the 30 stages                                                 */
/* ------------------------------------------------------------------ */

export const CLOSING_BLOCKS: SWBlock[] = [
  {
    type: "read",
    key: "sw-after-read",
    title: "Your journey continues",
    body: [
      "Attachment patterns don't switch off after thirty stages — and that was never the promise. What changes is how early you notice them, and how many choices you have once you do.",
      "Come back to any stage as often as you like. Your writing is all still here.",
    ],
  },
  {
    type: "questions",
    key: "sw-returning",
    title: "When I notice an old pattern",
    intro: "A short plan for the next time it shows up.",
    fields: [
      { key: "notice", label: "I'll know it's happening when…", rows: 3 },
      { key: "do", label: "The first thing I'll do is…", rows: 3 },
      { key: "stage", label: "The stage I'd return to is…", rows: 2 },
    ],
  },
];

/* ------------------------------------------------------------------ */
/* My Secure Within Journey — the aggregated private page              */
/* ------------------------------------------------------------------ */

export const JOURNEY_SECTIONS: { key: string; title: string; emoji: string }[] = [
  { key: "day2-attachment-explore", title: "Patterns I Recognise", emoji: "🔍" },
  { key: "day3-body", title: "Closeness & Distance in My Body", emoji: "🫂" },
  { key: "day4-attachment-fear", title: "My Attachment Triggers", emoji: "⚡" },
  { key: "day5-beliefs", title: "My Core Attachment Beliefs", emoji: "🪨" },
  { key: "day6-patterns", title: "My Relationship Patterns", emoji: "🔁" },
  { key: "my-attachment-map", title: "My Attachment Map", emoji: "🗺️" },
  { key: "my-attachment-story", title: "My Attachment Story", emoji: "✍️" },
  {
    key: "my-attachment-regulation-toolkit",
    title: "My Attachment Regulation Toolkit",
    emoji: "🧭",
  },
  { key: "my-boundaries", title: "My Boundaries", emoji: "🧱" },
  { key: "my-communication-tools", title: "Expressing My Needs", emoji: "🗣️" },
  { key: "my-secure-affirmations", title: "Words I Give Myself", emoji: "💛" },
  { key: "my-green-flags", title: "My Green Flags", emoji: "💚" },
  { key: "my-secure-blueprint", title: "My Secure Relationship Blueprint", emoji: "🏛️" },
  { key: "my-secure-within-closing", title: "Secure Within", emoji: "🤍" },
];

export const SAFETY_NOTICE =
  "Secure Within is educational self-help support. It is not therapy, diagnosis, medical advice or emergency care. Attachment patterns are for self-understanding only — they never explain or excuse how somebody else treated you. If an exercise becomes overwhelming, please stop, ground yourself, and come back when you feel ready.";

export const NO_STREAK_NOTICE =
  "There is no deadline and no streak. “30 days” describes the structure, not a schedule. Your journey is still here whenever you're ready.";
